// client/src/lib/walkthroughJourneys.ts
// Role-walkthrough journeys — same step shapes as the store setup wizard.
// Spec: docs/superpowers/specs/2026-07-07-role-walkthroughs-design.md
import type { SetupTourStep } from '@/lib/setupModules';

// Resolved by WalkthroughProvider against SubscriptionContext + useRewardsSystem.
// 'kitchen_dept' = store has the kitchen feature AND this user works kitchen
// (BOH department category) — a front-counter TM never tours the kitchen.
// Plain feature names ('kitchen', 'leadership', 'evaluations', 'documentation')
// resolve straight through isFeatureEnabled — leaders tour the kitchen
// regardless of their own department.
export type StopGate =
  | 'fohTasks' | 'training' | 'pathways' | 'rewards' | 'kitchen_dept'
  | 'kitchen' | 'leadership' | 'evaluations' | 'documentation'
  | 'safe_counting'; // store settings.safeConfig.enabled — not a subscription feature

export interface WalkthroughStop {
  route: string;
  label: string;
  nudge: string;
  gate?: StopGate;         // omitted = always shown
  tourSteps: SetupTourStep[];
}

export type WalkthroughJourneyKey = 'team_member' | 'trainer' | 'leader' | 'director' | 'admin';

export interface WalkthroughJourney {
  label: string;
  welcome: { title: string; sub: string };
  stops: WalkthroughStop[];
}

const TEAM_MEMBER_STOPS: WalkthroughStop[] = [
      {
        route: '/dashboard', label: 'Your Dashboard',
        nudge: 'Everything that matters for your day lives on this one screen.',
        tourSteps: [
          { anchor: 'tm-home-header', title: 'Your home screen', body: 'Every time you open the app, this is your day at a glance — and anything that needs you shows up right below.' }, // NEW anchor
          { anchor: 'tm-home-training-stat', title: 'Your training', body: 'How many training plans you have going — tap to jump straight into them.' }, // NEW
          { anchor: 'tm-home-performance', title: 'Your performance', body: 'Your latest evaluation score, or when your next review is coming — no surprises.' }, // NEW
          { anchor: 'tm-home-career', title: 'Your career journey', body: "Your path to the next role, with real progress. This is the same journey we'll visit in a moment." }, // NEW; gated inside page by promotionPathways — graceful-skip covers it
          // Mobile-only chrome (anchors don't exist on desktop — graceful-skip):
          { anchor: 'tm-mobile-nav', title: 'Your bottom bar', body: 'Your most-used pages, one tap away. You can change which shortcuts live here in Settings.' },
          { anchor: 'tm-mobile-menu', title: 'Everything else is in the menu', body: 'Tap here for the full menu — every page, plus your Quick Actions, which you can edit right from the menu.' }
        ]
      },
      {
        route: '/foh', label: 'Your Checklist', gate: 'fohTasks',
        nudge: 'The daily checklist your team runs on — try checking something off.',
        tourSteps: [
          { anchor: 'foh-shifts', title: 'One list per shift', body: 'Opening, Transition, Closing, plus any shifts your store adds — tap one to see its tasks. The tab shows how much is already done.' },
          {
            anchor: 'foh-task-list', title: 'Your turn — check one off',
            body: 'This is the real list your store runs on. Tap the checkbox on any task to complete it.',
            interactive: {
              prompt: 'Go ahead — tap a task now',
              doneEvent: 'ldg:foh-task-completed', revertEvent: 'ldg:tour-revert-foh',
              celebrate: { anchor: 'foh-progress', title: "🎉 That's it", body: "Every check-off updates the team's progress bar live. (We'll uncheck this demo one for you.)" }
            }
          },
          { anchor: 'foh-progress', title: 'The team score', body: 'This bar is what everyone glances at mid-rush — when it fills, the shift is handled.' }
        ]
      },
      {
        route: '/kitchen/checklists', label: 'Kitchen Checklists', gate: 'kitchen_dept',
        nudge: 'The kitchen runs on these shift checklists — opening, transition, closing.',
        tourSteps: [
          { anchor: 'tm-kitchen-tabs', title: 'Pick your shift', body: "Each shift has its own list — Opening, Transition, Closing, plus any your store adds. The tab shows how much is already done." },
          { anchor: 'tm-kitchen-list', title: 'Your kitchen tasks', body: 'Work down the list as you go — the food safety and quality tasks the whole kitchen shares.' },
          { anchor: 'tm-kitchen-complete', title: 'Tap to complete', body: 'Tap anywhere on a task to check it off — everyone sees the progress instantly.' }
        ]
      },
      {
        route: '/kitchen/food-safety', label: 'Food Safety', gate: 'kitchen_dept',
        nudge: 'Daily safety checks and temperature logs — the record builds itself as you work.',
        tourSteps: [
          { anchor: 'kitchen-checklists', title: 'Daily safety tasks', body: 'The food safety checklist for each part of the day — every completion is stamped with who and when.' },
          { anchor: 'kitchen-temps', title: 'Record temperatures', body: 'Log equipment and product temps for your shift, all in one form — this is the record inspectors love to see.' }
        ]
      },
      {
        route: '/kitchen/waste-tracker', label: 'Waste Tracker', gate: 'kitchen_dept',
        nudge: 'Log waste as it happens — two taps, and the store sees where money goes.',
        tourSteps: [
          { anchor: 'kitchen-waste-2', title: "Today's total", body: 'The running dollar total of waste logged today — logging it is how the store finds and fixes the causes.' },
          { anchor: 'kitchen-waste-7', title: 'Meal period', body: 'Pick Breakfast, Lunch, or Dinner so entries land on the right shift.' },
          { anchor: 'kitchen-waste-8', title: 'Quick-add items', body: 'Tap an item tile to log one instantly — prices are already set for you.' },
          { anchor: 'kitchen-waste-9', title: 'Tag the reason', body: 'Overproduction, quality, expired, dropped — the reason is what turns entries into fixes.' },
          { anchor: 'kitchen-waste-11', title: "Today's entries", body: 'Everything logged today — tap the X if you logged something by mistake.' }
        ]
      },
      {
        route: '/kitchen/equipment', label: 'Kitchen Equipment', gate: 'kitchen_dept',
        nudge: 'Every unit in the kitchen — see what needs attention and report problems fast.',
        tourSteps: [
          { anchor: 'kitchen-equip-1', title: 'Equipment health', body: 'How many units are running versus the total — red numbers mean something needs attention.' },
          { anchor: 'kitchen-equip-8', title: 'Each unit at a glance', body: "Every card is one unit — its status, schedules, and quick actions." },
          { anchor: 'kitchen-equip-12', title: 'Report an issue', body: "Fryer acting up? Flag it here — it stays flagged until it's fixed, so nothing gets forgotten between shifts." }
        ]
      },
      {
        route: '/team-development', label: 'My Pathway', gate: 'pathways',
        nudge: 'Your track to the next role — every step, visible.',
        tourSteps: [
          { anchor: 'tm-pathway-progress', title: 'Your track', body: 'This is your path to the next role — each step is something concrete: training, showing your skills, or a sign-off.' }, // NEW
          { anchor: 'tm-pathway-next', title: 'Your next step', body: "You always know exactly what's next. Complete it and the bar moves." }, // NEW
          { anchor: 'tm-pathway-mastery', title: 'Show what you know', body: 'Mark the positions you\'ve mastered and the ones you want to learn — your leaders see it and can line up training.' } // NEW
        ]
      },
      {
        route: '/training', label: 'Your Training', gate: 'training',
        nudge: 'Your assigned training plans and exactly where you left off.',
        tourSteps: [
          { anchor: 'tm-training-plans', title: 'Your plans', body: 'Every training plan assigned to you, with progress. Tap one to see its days and tasks.' },
          { anchor: 'tm-training-next', title: 'Pick up where you left off', body: 'Your next task is always called out — a few minutes at a time adds up fast.' },
          // Anchored to the section tab on desktop and to the header's section switcher
          // on mobile, so keep the wording surface-neutral rather than "this tab".
          { anchor: 'tm-training-assess', title: 'Your assessments', body: 'Skill and knowledge assessments assigned to you live under My Assessments — when one is due, this is where you take it.' }
        ]
      },
      {
        route: '/rewards', label: 'Rewards', gate: 'rewards',
        nudge: 'Earn points for great work, spend them on real rewards.',
        tourSteps: [
          { anchor: 'rewards-points', title: 'Your balance', body: 'CFAdollars you\'ve earned — leaders award them for great work, milestones, and wins.' },
          { anchor: 'rewards-catalog', title: 'The rewards catalog', body: 'What your store offers — tap anything to redeem when you have the points.' },
          { anchor: 'rewards-history', title: 'Your history', body: 'Every award and redemption, so your balance always makes sense.' }
        ]
      },
      // NOTE: no Team Chat stop — chat is self-explanatory and its locked
      // full-viewport layout (especially installed-PWA mode) fights the tour
      // chrome. Removed after Ashley's live run 2026-07-08.
      {
        route: '/quick-links', label: 'Quick Links',
        nudge: 'Store resources one tap away — links your leaders keep current.',
        tourSteps: [
          { anchor: 'tm-links-grid', title: 'Store links', body: 'Everything your leaders want handy — schedules, forms, resources — collected here so nobody hunts through old texts.' },
          { anchor: 'tm-links-item', title: 'Tap to open', body: 'Each card opens its link. If something you need is missing, tell a leader — they can add it.' }
        ]
      },
      {
        route: '__SELF_PROFILE__', label: 'My Profile', // provider substitutes /users/<their id>
        nudge: 'Your info, your record, and where this tour lives if you ever want it again.',
        tourSteps: [
          // Step order follows DOM order (hero → info → links) so the tour
          // flows down the page instead of jumping back up at the finale.
          { anchor: 'tm-profile-hero', title: 'Your profile', body: 'Your position, departments, time with the store, and your latest score — all in one place.' },
          { anchor: 'tm-profile-info', title: 'Your info', body: 'Start date, shift, and how to reach you — tell a leader if anything here is out of date.' },
          { anchor: 'tm-profile-links', title: "That's the tour!", body: 'Jump to your growth path, training, or documents from here anytime. And if you ever want a refresher — the How-To guide and this tour both live in the menu, top right.' }
        ]
      }
];

// Trainer = the team-member journey plus the trainer's training world: the
// Team Training pool card on the dashboard, and the manager-side training
// pages (shared sign-off pool — any trainer can complete anyone's training).
const TRAINER_STOPS: WalkthroughStop[] = TEAM_MEMBER_STOPS.map((stop) => {
  if (stop.route === '/dashboard') {
    return {
      ...stop,
      tourSteps: [
        ...stop.tourSteps.slice(0, 4),
        { anchor: 'tm-home-team-training', title: 'Team Training', body: "Everyone currently in training at your store. Training sign-off is a shared pool — any trainer can work with and check off any trainee." },
        ...stop.tourSteps.slice(4) // mobile nav + menu steps stay last
      ]
    };
  }
  if (stop.route === '/training') {
    return {
      route: '/training/progress', label: 'Team Training', gate: 'training' as const,
      nudge: "Your trainees' progress — the whole store's training runs through here.",
      tourSteps: [
        { anchor: 'training-progress-2', title: 'The scoreboard', body: 'Active trainees, average completion, and new hires without a plan — your training day at a glance.' },
        { anchor: 'training-progress-1', title: 'Your trainee list', body: 'Every team member in training, with their plan and progress bar — tap a row to check off tasks as you train them.' },
        { anchor: 'training-progress-6', title: 'Active vs completed', body: 'Flip between people still in training and those who finished.' },
        { anchor: 'training-progress-7', title: 'Assign training', body: 'Put any team member on a plan with a start date — their progress shows up here immediately.' }
      ]
    };
  }
  return stop;
});

// The New Hires page — inserted right after Team Training for trainers.
const TRAINER_NEW_HIRES_STOP: WalkthroughStop = {
  route: '/training/new-hires', label: 'New Hires', gate: 'training',
  nudge: 'Nobody slips through their first weeks without a plan.',
  tourSteps: [
    { anchor: 'training-hires-1', title: 'Your newest people', body: 'Anyone hired in the last 60 days shows up here automatically.' },
    { anchor: 'training-hires-2', title: 'Start their training', body: 'One tap opens the plan picker — a plan in week one is the biggest retention lever there is.' }
  ]
};
const trainerTrainingIdx = TRAINER_STOPS.findIndex((s) => s.route === '/training/progress');
TRAINER_STOPS.splice(trainerTrainingIdx + 1, 0, TRAINER_NEW_HIRES_STOP);

// ── Leader journey ───────────────────────────────────────────────────────────
// Leaders run shifts across the whole store, so kitchen stops gate on the
// FEATURE only (not their department), and they get the leader-side pages:
// safe counts, guest recovery, their leadership area (real guided-create),
// development plans, team training, evaluations, and team development.
const LEADER_STOPS: WalkthroughStop[] = [
  {
    route: '/dashboard', label: 'Your Dashboard',
    nudge: 'Your daily command center — priorities, live numbers, quick actions.',
    tourSteps: [
      { anchor: 'home-hero', title: 'Your command center', body: 'Every day starts here — your priorities, your numbers, and everything that needs you.' },
      { anchor: 'home-priorities', title: 'My Priorities', body: 'Evaluations coming due, follow-ups, acknowledgments — it all lands here automatically and clears as you knock it out.' },
      { anchor: 'home-stat-cards', title: 'Live store insights', body: 'Checklists, training, evaluations — real numbers, tap any card to jump in, or Customize to pick what you watch.' },
      { anchor: 'home-quick-actions', title: 'Quick Actions', body: 'One-tap shortcuts to your most-used tools — customize the grid to match your day.' },
      { anchor: 'tm-mobile-nav', title: 'Your bottom bar', body: 'Your most-used pages, one tap away — change which shortcuts live here in Settings.' },
      { anchor: 'tm-mobile-menu', title: 'Everything else is in the menu', body: 'The full menu — every page, plus your editable Quick Actions.' }
    ]
  },
  {
    route: '/foh', label: 'FOH Checklists', gate: 'fohTasks',
    nudge: 'The daily lists your team runs on — you own what goes on them.',
    tourSteps: [
      { anchor: 'foh-shifts', title: 'One list per shift', body: 'Opening, Transition, Closing, plus any shifts your store adds — each tab shows how much is done.' },
      {
        anchor: 'foh-task-list', title: 'Your turn — check one off',
        body: 'The real list. Tap any checkbox — and as a leader you can also drag the handles to reorder.',
        interactive: {
          prompt: 'Go ahead — tap a task now',
          doneEvent: 'ldg:foh-task-completed', revertEvent: 'ldg:tour-revert-foh',
          celebrate: { anchor: 'foh-progress', title: "🎉 That's it", body: "Every check-off updates the team's progress live. (We'll uncheck this demo one for you.)" }
        }
      },
      { anchor: 'foh-add-task', title: 'Add your own tasks', body: 'Tap + to add a task to the current shift — the whole team sees it right away.' },
      { anchor: 'foh-settings', title: 'Checklist settings', body: 'Require initials on completions for accountability, or show position labels — your call.' },
      { anchor: 'foh-history', title: 'Look back anytime', body: "Any past day's completions — what got done, when, and by whom." }
    ]
  },
  {
    route: '/kitchen/checklists', label: 'Kitchen Checklists', gate: 'kitchen',
    nudge: 'The kitchen shift lists — same rhythm as FOH.',
    tourSteps: [
      { anchor: 'tm-kitchen-tabs', title: 'Pick your shift', body: 'Opening, Transition, Closing — each with a live completion count.' },
      { anchor: 'tm-kitchen-list', title: 'The kitchen tasks', body: 'Food safety and quality tasks the whole kitchen shares — leaders can add and edit them right on this page.' }
    ]
  },
  {
    route: '/kitchen/food-safety', label: 'Food Safety', gate: 'kitchen',
    nudge: 'Daily checks and temps — your inspection record builds itself.',
    tourSteps: [
      { anchor: 'kitchen-checklists', title: 'Daily safety tasks', body: 'Every completion stamped with who and when — a clean record with zero paperwork.' },
      { anchor: 'kitchen-temps', title: 'Temperature logs', body: 'Equipment and product temps for each shift, all in one form.' }
    ]
  },
  {
    route: '/kitchen/waste-tracker/analytics', label: 'Waste Analytics', gate: 'kitchen',
    nudge: 'Where the money goes — and the patterns to fix.',
    tourSteps: [
      { anchor: 'kitchen-waste-analytics-1', title: 'The headline numbers', body: "Today, this week, yesterday, and the top wasted item." },
      { anchor: 'kitchen-waste-analytics-2', title: 'Waste goals', body: 'Set daily, weekly, and monthly cost targets — the progress bar keeps the team honest.' },
      { anchor: 'kitchen-waste-analytics-7', title: 'Your costliest items', body: 'The five items burning the most money — expand any to see every entry.' }
    ]
  },
  {
    route: '/safe-counting', label: 'Safe Counting', gate: 'safe_counting',
    nudge: 'Every shift, every drawer, on record.',
    tourSteps: [
      { anchor: 'safe-shift-tabs', title: 'Three counts a day', body: 'Opening, Transition, Closing — the page auto-selects the current shift.' },
      { anchor: 'safe-variance', title: 'Expected vs. actual', body: 'Green means balanced; a red difference is worth a second look before you save.' },
      { anchor: 'safe-history', title: 'Past counts', body: 'Every count by day and shift — patterns in overages and shortages show up fast.' }
    ]
  },
  {
    route: '/customer-complaints', label: 'Guest Recovery',
    nudge: 'Every guest concern logged, followed up, and closed.',
    tourSteps: [
      { anchor: 'complaints-new', title: 'Log a guest concern', body: 'Name, phone, what was missed, estimated cost — one place instead of sticky notes.' },
      { anchor: 'complaints-list', title: 'The recovery queue', body: 'Orange = open and waiting on follow-up; green = resolved. Nothing slips through a shift change.' },
      { anchor: 'complaints-stats', title: 'Your recovery snapshot', body: 'Volume, open items, recovery cost, and repeat guests to watch.' }
    ]
  },
  {
    route: '/leadership/dashboard', label: 'Your Leadership Area', gate: 'leadership',
    nudge: "Pick the business area you own and set it up for real — if it's already set up, just hit Next.",
    tourSteps: [
      {
        anchor: 'leadership-wizard', title: 'Set up your real area',
        body: "Pick a business area you own — Facilities, Kitchen, Talent, or custom — then choose the metrics and tasks that matter. This one's for keeps: it becomes your live dashboard.",
        interactive: {
          prompt: 'Go ahead — set up your area now',
          doneEvent: 'ldg:leadership-area-created',
          celebrate: { anchor: 'leadership-metrics', title: '🎉 Your area is live', body: 'This dashboard is yours now — real metrics, real tasks. Hit Next on the red bar to keep going.' }
        }
      }
    ]
  },
  {
    route: '/leadership/dashboard', label: 'Your Area Dashboard', gate: 'leadership',
    nudge: 'Everything your area gives you — metrics, focus priorities, tasks, and notes.',
    tourSteps: [
      { anchor: 'leadership-metrics', title: 'Metrics that matter', body: 'A few key numbers so you always know where you stand — tap one to log a value.' },
      { anchor: 'leadership-focus', title: 'Your focus priorities', body: "The 1–3 things you're driving this week." },
      { anchor: 'leadership-tasks', title: 'Daily leader tasks', body: 'Recurring tasks that keep your area moving — check them off like any checklist.' },
      { anchor: 'leadership-notes', title: 'Your leader meetings', body: 'Record each meeting round the room — every action item lands on its owner\'s checklist.' }
    ]
  },
  {
    route: '/leadership/development-plans', label: 'Development Plans', gate: 'leadership',
    nudge: 'A leadership library, ready to go — enroll and lessons unlock immediately.',
    tourSteps: [
      { anchor: 'devplans-1', title: 'A leadership library', body: 'Ready-made plans — from Heart of Leadership to operational excellence. Pick one that fits where you want to grow.' },
      { anchor: 'devplans-2', title: 'Start with one tap', body: 'Hit Start Plan — lessons unlock right away, one plan at a time so it gets finished.' }
    ]
  },
  {
    route: '/training/progress', label: 'Team Training', gate: 'training',
    nudge: "Your store's training, all in one place.",
    tourSteps: [
      { anchor: 'training-progress-2', title: 'The scoreboard', body: 'Active trainees, average completion, and new hires without a plan.' },
      { anchor: 'training-progress-1', title: 'The trainee list', body: 'Everyone in training with their plan and progress — tap a row for task-by-task detail.' },
      { anchor: 'training-progress-7', title: 'Assign training', body: 'Put any team member on a plan with a start date.' }
    ]
  },
  {
    route: '/evaluations', label: 'Evaluations', gate: 'evaluations',
    nudge: 'Consistent, on-time reviews — the templates are ready.',
    tourSteps: [
      { anchor: 'evals-templates', title: 'The evaluation hub', body: 'Evaluations, Scheduled reviews, Templates, and Analytics all live behind this tab bar.' },
      { anchor: 'evals-list', title: 'Reviews in flight', body: 'Each card shows the employee, template, status, and what to do next.' },
      { anchor: 'evals-new', title: 'Start a review', body: 'Pick a team member and a template — scheduling and reminders are handled for you.' }
    ]
  },
  {
    route: '/team-development/overview', label: 'Team Development', gate: 'leadership',
    nudge: 'Every team member’s growth — tracks, readiness, and day-to-day coaching.',
    tourSteps: [
      { anchor: 'teamdev-team-grid', title: "Your team's growth", body: 'Every team member with their track progress and promotion readiness.' },
      { anchor: 'teamdev-focus', title: 'Coach day to day', body: 'Jot things to work on and set the slider for how they’re doing — lightweight coaching that sticks.' }
    ]
  },
  {
    route: '__SELF_PROFILE__', label: 'My Profile',
    nudge: 'Your info, your record, and where this tour lives if you ever want it again.',
    tourSteps: [
      { anchor: 'tm-profile-hero', title: 'Your profile', body: 'Your position, departments, tenure, and latest score.' },
      { anchor: 'tm-profile-links', title: "That's the tour!", body: 'Your growth path, training, and documents are one tap away here. Replay this tour anytime from the profile menu, top right.' }
    ]
  }
];

// ── Director journey ─────────────────────────────────────────────────────────
// Leader journey + the oversight layer: My Leaders coaching, discipline
// documentation, and evaluation analytics.
const DIRECTOR_STOPS: WalkthroughStop[] = (() => {
  const stops = [...LEADER_STOPS];
  const devPlansIdx = stops.findIndex((s) => s.route === '/leadership/development-plans');
  stops.splice(devPlansIdx + 1, 0, {
    route: '/leadership/my-leaders', label: 'My Leaders', gate: 'leadership',
    nudge: 'Coach the leaders who run your areas — metrics, 1:1s, and notes in one place.',
    tourSteps: [
      { anchor: 'ml-1', title: 'Your leaders at a glance', body: 'Each card is one leader you oversee — their metrics, plan, and how recently you coached them.' },
      { anchor: 'ml-2', title: 'Tap a card to coach', body: 'The full picture: metrics, tasks, development, focus items, and your coaching history.' },
      { anchor: 'ml-3', title: 'Who oversees whom', body: 'An admin assigns each overseer their leaders — once you have leaders assigned, they appear here.' }
    ]
  });
  const teamDevIdx = stops.findIndex((s) => s.route === '/team-development/overview');
  stops.splice(teamDevIdx + 1, 0, {
    route: '/documentation', label: 'Documentation', gate: 'documentation',
    nudge: 'Write-ups, PIPs, and records — a clear paper trail the day you need it.',
    tourSteps: [
      { anchor: 'docs-list', title: 'Records by team member', body: 'Everything groups by person — their full documentation history in one place.' },
      { anchor: 'docs-4-category', title: 'Filter by type', body: 'All, Disciplinary, PIP, or Administrative.' },
      { anchor: 'docs-4-analytics', title: 'Discipline analytics', body: 'Trends and risk flags across the team — spot patterns before they become problems.' }
    ]
  }, {
    route: '/analytics/evaluation-trends', label: 'Evaluation Analytics', gate: 'evaluations',
    nudge: 'How reviews are trending across the store.',
    tourSteps: [
      { anchor: 'evals-trends-1', title: 'Pick your window', body: 'Timeframe, view, and template drive every chart on this page.' },
      { anchor: 'evals-trends-2', title: 'The headline numbers', body: 'Completed, pending, and average scores at a glance.' }
    ]
  });
  return stops;
})();

// ── Admin orientation ────────────────────────────────────────────────────────
// A second admin joins a store that's ALREADY set up — tour-only, no
// decisions. Director journey + the admin-owned surfaces: User Management
// and a Getting Started hand-off in the finale.
const ADMIN_STOPS: WalkthroughStop[] = (() => {
  const stops = [...DIRECTOR_STOPS];
  const recoveryIdx = stops.findIndex((s) => s.route === '/customer-complaints');
  stops.splice(recoveryIdx + 1, 0, {
    route: '/users', label: 'Team Members',
    nudge: 'Your whole roster — accounts, roles, imports, and access.',
    tourSteps: [
      { anchor: 'team-1', title: 'Your roster', body: 'Every team member with their role, department, and manager — tap a row to view or edit.' },
      { anchor: 'team-6', title: 'Import & export tools', body: 'Bulk-import from CSV, download the template, or manage access and multi-store permissions.' },
      { anchor: 'team-9', title: 'Row actions', body: 'Edit details, reset a password, resend a welcome email, or offboard someone.' }
    ]
  });
  // Admin finale hands off to the Getting Started hub they now co-own.
  const profile = stops[stops.length - 1];
  stops[stops.length - 1] = {
    ...profile,
    tourSteps: [
      ...profile.tourSteps.slice(0, -1),
      { anchor: 'tm-profile-links', title: "That's the tour!", body: 'One more thing: the Getting Started hub (under Setup in the menu) tracks every section of your store — revisit or reconfigure anything there. Replay this tour anytime from the profile menu.' }
    ]
  };
  return stops;
})();

export const WALKTHROUGH_JOURNEYS: Record<WalkthroughJourneyKey, WalkthroughJourney> = {
  team_member: {
    label: 'Team member tour',
    welcome: {
      title: 'Welcome to LD Growth',
      sub: 'Your new home for your shifts, your growth, and your wins — a quick tour takes about five minutes.'
    },
    stops: TEAM_MEMBER_STOPS
  },
  trainer: {
    label: 'Trainer tour',
    welcome: {
      title: 'Welcome to LD Growth',
      sub: "Your new home for your shifts, your growth — and the people you're training. A quick tour takes about five minutes."
    },
    stops: TRAINER_STOPS
  },
  leader: {
    label: 'Leader tour',
    welcome: {
      title: 'Welcome to LD Growth',
      sub: 'Your new home for running the shift — checklists, cash, your team, and your own leadership area. About ten minutes, and you can pause anytime.'
    },
    stops: LEADER_STOPS
  },
  director: {
    label: 'Director tour',
    welcome: {
      title: 'Welcome to LD Growth',
      sub: 'Your new home for running the store — daily operations, your leaders, and the numbers behind all of it. About ten minutes, and you can pause anytime.'
    },
    stops: DIRECTOR_STOPS
  },
  admin: {
    label: 'Admin orientation',
    welcome: {
      title: 'Welcome to LD Growth',
      sub: "This store is already set up — here's a tour of everything it runs, plus the admin tools that are now yours. About ten minutes, and you can pause anytime."
    },
    stops: ADMIN_STOPS
  }
};
